export type LeadLike = {
  affiliate_id: string;
  status: string;
  created_at: string;
  updated_at?: string | null;
};

export type AffiliateMetrics = {
  total: number;
  newCount: number;
  contacted: number;
  closed: number;
  conversionPct: number; // closed / total
  responsePct: number; // (contacted + closed) / total
  avgResponseHours: number | null;
  lastActivity: string | null;
};

export const WINDOWS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "All time", days: null as number | null },
];

export function filterByWindow<T extends { created_at: string }>(
  rows: T[],
  windowDays: number | null,
): T[] {
  if (!windowDays) return rows;
  const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000;
  return rows.filter((r) => new Date(r.created_at).getTime() >= cutoff);
}

export function computeMetrics(leads: LeadLike[]): AffiliateMetrics {
  const total = leads.length;
  const newCount = leads.filter((l) => l.status === "new").length;
  const contacted = leads.filter((l) => l.status === "contacted").length;
  const closed = leads.filter((l) => l.status === "closed").length;

  const responded = leads.filter((l) => l.status !== "new" && l.updated_at);
  const avgResponseMs = responded.length
    ? responded.reduce(
        (sum, l) =>
          sum +
          Math.max(
            0,
            new Date(l.updated_at as string).getTime() -
              new Date(l.created_at).getTime(),
          ),
        0,
      ) / responded.length
    : null;

  const lastActivityMs = leads.reduce<number>((max, l) => {
    const t = new Date(l.updated_at || l.created_at).getTime();
    return t > max ? t : max;
  }, 0);

  return {
    total,
    newCount,
    contacted,
    closed,
    conversionPct: total ? (closed / total) * 100 : 0,
    responsePct: total ? ((contacted + closed) / total) * 100 : 0,
    avgResponseHours: avgResponseMs != null ? avgResponseMs / 1000 / 3600 : null,
    lastActivity: lastActivityMs ? new Date(lastActivityMs).toISOString() : null,
  };
}

export function metricsByAffiliate(
  leads: LeadLike[],
  windowDays: number | null,
): Record<string, AffiliateMetrics> {
  const filtered = filterByWindow(leads, windowDays);
  const grouped: Record<string, LeadLike[]> = {};
  for (const l of filtered) {
    (grouped[l.affiliate_id] ||= []).push(l);
  }
  const out: Record<string, AffiliateMetrics> = {};
  for (const [id, rows] of Object.entries(grouped)) {
    out[id] = computeMetrics(rows);
  }
  return out;
}